// src/components/sections/TrainingSection.jsx
import React, { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent, Typography, Chip, CircularProgress, Box, Stack, Tooltip, Divider, IconButton, Button, useTheme, useMediaQuery } from "@mui/material";
import { GraduationCap, Radio, BookOpen, Clock, Star, Users, ArrowRight, IndianRupee, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import SectionHeading from "../../components/ui/SectionHeading";
import { fadeUp } from "../../utils/animations";
import { getPublicTrainings } from "../../api/trainings";

const MODE_FILTERS = [
  { label: "All", value: "" },
  { label: "Classroom", value: "classroom" },
  { label: "Live Online", value: "liveonline" },
  { label: "Self-paced", value: "selfpaced" },
];



// Fallback icon + gradient by mode, same pattern as RoadmapSection's ICON_META
const MODE_META = {
  classroom:  { icon: GraduationCap, gradient: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" },
  liveonline: { icon: Radio,         gradient: "linear-gradient(135deg, #FFF1F2, #FFE4E6)" },
  selfpaced:  { icon: BookOpen,      gradient: "linear-gradient(135deg, #F0FDF4, #DCFCE7)" },
  default:    { icon: GraduationCap, gradient: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" },
};

const CARD_HEIGHT = 470;
const CARD_MEDIA_HEIGHT = 190;
const CARD_GAP = 24; // px, keep in sync with the gap set on the track below

function formatPrice(price, currency) {
  if (price == null) return null;
  const symbol = currency === "USD" ? "$" : "₹";
  return `${symbol}${Number(price).toLocaleString()}`;
}

// Thumbnail with the same fallback behavior as PathThumb: real image when
// available, icon + gradient tile when there's no image or it fails to load.
function TrainingThumb({ src, mode, isFeatured }) {
  const [failed, setFailed] = useState(!src);
  const meta = MODE_META[mode] ?? MODE_META.default;
  const Icon = meta.icon;

  return (
    <Box
      className="card-media card-media--cover"
      sx={{
        height: CARD_MEDIA_HEIGHT,
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        background: failed ? meta.gradient : "#F1F5FB",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!failed && src ? (
        <Box
          component="img"
          src={src}
          alt=""
          onError={() => setFailed(true)}
          className="training-thumb-img"
          sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease" }}
        />
      ) : (
        <Icon size={36} color="#1d4ed8" strokeWidth={1.5} />
      )}

      {isFeatured && (
        <Chip
          icon={<Star size={12} />}
          label="Featured"
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "#111827",
            color: "white",
            fontWeight: 600,
            fontSize: "0.65rem",
          }}
        />
      )}
    </Box>
  );
}

// Internal navigation only now — Enroll always opens the dedicated /enroll page.
function EnrollButton({ label, to, className = "training-enroll-btn" }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      onClick={(e) => e.stopPropagation()}
      className={className}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        width: "100%",
        py: 1.35,
        borderRadius: "12px",
        fontWeight: 700,
        fontSize: 14.5,
        color: "white",
        textDecoration: "none",
        background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
        boxShadow: "0 10px 22px -10px rgba(37,99,235,0.6)",
        transition: "transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: "0 14px 28px -10px rgba(37,99,235,0.75)",
          background: "linear-gradient(135deg, #2E6EF0 0%, #2050E0 100%)",
        },
      }}
    >
      {label}
      <ArrowRight size={16} style={{ transition: "transform 0.2s ease" }} />
    </Box>
  );
}

// Flip card: front matches the original card layout, back reveals a fuller
// description + the same enrollment CTA. Flips on hover for pointer devices;
// on touch devices (no hover), tapping the info button toggles the flip so
// the detail side is still reachable.
//
// IMPORTANT: this component does NOT own its own width. Its parent — the
// motion.div that is a direct child of the flex track — sets flex-basis via
// a calc() based on cardsPerView. If this component also applied that same
// calc() to itself, the division would happen twice (once against the
// track's width to size the slot, then again against the slot's own width
// to size the card inside it), producing a tiny card floating in a mostly
// empty slot. So here we just fill 100% of whatever slot we're given.
function TrainingCard({ t }) {
  const [flipped, setFlipped] = useState(false);
  const navigate = useNavigate();
  const priceLabel = formatPrice(t.price, t.currency);
  const blurb = t.shortDescription || "";
  const fullDescription = t.description || t.shortDescription || "No additional details are available for this program yet.";
  const detailHref = `/public/trainings/slug/${t.slug}`;
  const enrollHref = `/enroll?training=${encodeURIComponent(t.slug || "")}&title=${encodeURIComponent(t.title || "")}`;

  const handleInfoToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFlipped((f) => !f);
  };

  const handleCardClick = () => {
    if (t.slug) navigate(detailHref);
  };

  return (
    <Box
      className="training-carousel-card"
      onClick={handleCardClick}
      sx={{
        width: "100%",
        height: CARD_HEIGHT,
        scrollSnapAlign: "start",
        perspective: "1600px",
        cursor: t.slug ? "pointer" : "default",
      }}
    >
      <Box
        className={`flip-card-inner${flipped ? " is-flipped" : ""}`}
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          transition: "transform 0.6s cubic-bezier(0.4, 0.2, 0.2, 1)",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
          "@media (hover: hover)": {
            ".training-flip-wrap:hover &": { transform: "rotateY(180deg)" },
          },
        }}
      >
        {/* FRONT */}
        <Card
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            border: "1px solid rgba(15,23,42,0.09)",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            overflow: "hidden",
          }}
        >
          <TrainingThumb src={t.imageUrl} mode={t.mode} isFeatured={t.featured} />

          <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
            <CardContent
              sx={{
                flex: 1,
                display: "grid",
                gridTemplateRows: "30px 44px 40px 1fr auto",
                rowGap: 0,
                pl: 2.25,
                pr: 2.5,
                pt: 2.25,
                pb: 2.5,
                minHeight: 0,
                minWidth: 0,
                overflow: "hidden",
                "&:last-child": { pb: 2.5 },
              }}
            >
              {/* Row 1: badges */}
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ flexWrap: "nowrap" }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ overflow: "visible" }}>
                  {t.badge && (
                    <Chip
                      label={t.badge}
                      size="small"
                      sx={{ bgcolor: "#111827", color: "white", fontWeight: 600, fontSize: "0.65rem", flexShrink: 0 }}
                    />
                  )}
                  {t.mode && (
                    <Chip
                      label={MODE_FILTERS.find((m) => m.value === t.mode)?.label || t.mode}
                      size="small"
                      sx={{ fontWeight: 600, fontSize: "0.65rem", bgcolor: "#EFF6FF", color: "primary.main", flexShrink: 0 }}
                    />
                  )}
                </Stack>
                <IconButton
                  size="small"
                  aria-label="Show more details"
                  onClick={handleInfoToggle}
                  sx={{
                    display: { xs: "inline-flex", md: "none" },
                    width: 26,
                    height: 26,
                    color: "#2563EB",
                    bgcolor: "#EFF6FF",
                    "&:hover": { bgcolor: "#DBEAFE" },
                  }}
                >
                  <Info size={14} />
                </IconButton>
              </Stack>

              {/* Row 2: title */}
              <Box sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                <Tooltip title={t.title} arrow placement="top" enterDelay={400}>
                  <Typography
                    className="training-card-title"
                    sx={{
                      fontWeight: 700,
                      fontSize: "1.05rem",
                      color: "#0B1530",
                      lineHeight: "22px",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      width: "100%",
                      wordBreak: "break-word",
                    }}
                  >
                    {t.title}
                  </Typography>
                </Tooltip>
              </Box>

              {/* Row 3: tagline */}
              <Box sx={{ overflow: "hidden" }}>
                               <Typography
                  sx={{
                    fontSize: 13.5,
                    color: "#64748B",
                    lineHeight: "20px",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    width: "100%",
                    wordBreak: "break-word",
                  }}
                >
                  {blurb}
                </Typography>
              </Box>

              {/* Row 4: flexible spacer */}
              <Box />

              {/* Row 5: footer */}
              <Box>
                <Divider sx={{ mb: 1.5 }} />
                <Stack direction="row" spacing={2.5} sx={{ mb: 1.5, flexWrap: "wrap" }}>
                  {t.duration && (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Clock size={15} color="#2563EB" />
                      </Box>
                      <Stack spacing={0}>
                        <Typography sx={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 600, lineHeight: 1.3 }}>Duration</Typography>
                        <Typography sx={{ fontSize: 13, color: "#0B1530", fontWeight: 700, lineHeight: 1.3 }}>{t.duration}</Typography>
                      </Stack>
                    </Stack>
                  )}
                  {priceLabel && (
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#EFF6FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <IndianRupee size={15} color="#2563EB" />
                      </Box>
                      <Stack spacing={0}>
                        <Typography sx={{ fontSize: 10.5, color: "#94A3B8", fontWeight: 600, lineHeight: 1.3 }}>Fee</Typography>
                        <Typography sx={{ fontSize: 13, color: "#0B1530", fontWeight: 700, lineHeight: 1.3 }}>{priceLabel}</Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
                {(t.rating != null || t.enrollmentCount != null) && (
                  <Stack direction="row" spacing={1.5} sx={{ mb: 1.25 }}>
                    {t.rating != null && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Star size={13} color="#F59E0B" />
                        <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>{t.rating}</Typography>
                      </Stack>
                    )}
                    {t.enrollmentCount != null && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Users size={13} color="#94A3B8" />
                        <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>{t.enrollmentCount}+</Typography>
                      </Stack>
                    )}
                  </Stack>
                )}
                                <EnrollButton label={t.ctaLabel || "Enroll now"} to={enrollHref} />
              </Box>
            </CardContent>
          </Box>
        </Card>

        {/* BACK */}
        <Card
          sx={{
            position: "absolute",
            inset: 0,
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            display: "flex",
            flexDirection: "column",
            borderRadius: "12px",
            border: "1px solid rgba(15,23,42,0.09)",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            overflow: "hidden",
            background: "linear-gradient(160deg, #0B1530 0%, #12213F 100%)",
            color: "white",
          }}
        >
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              p: 3,
              "&:last-child": { pb: 3 },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              {t.mode && (
                <Chip
                  label={MODE_FILTERS.find((m) => m.value === t.mode)?.label || t.mode}
                  size="small"
                  sx={{ fontWeight: 600, fontSize: "0.65rem", bgcolor: "rgba(255,255,255,0.12)", color: "white" }}
                />
              )}
              {t.rating != null && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Star size={13} color="#F59E0B" />
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.8)", fontWeight: 600 }}>{t.rating}</Typography>
                </Stack>
              )}
            </Stack>

            <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: "22px", mb: 1.25 }}>
              {t.title}
            </Typography>
<Box sx={{ flex: 1, minHeight: 0, position: "relative", overflow: "hidden" }}>
  <Typography
    sx={{
      fontSize: 13.5,
      color: "rgba(255,255,255,0.78)",
      lineHeight: "20px",
    }}
  >
    {fullDescription}
  </Typography>

  {/* fade mask instead of a hardcoded line-clamp count */}
  <Box
    sx={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: 28,
      background: "linear-gradient(to bottom, rgba(11,21,48,0), #0B1530)",
      pointerEvents: "none",
    }}
  />
</Box>

            <Stack direction="row" spacing={2.5} sx={{ my: 2, flexWrap: "wrap" }}>
              {t.duration && (
                <Stack spacing={0}>
                  <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>Duration</Typography>
                  <Typography sx={{ fontSize: 13, color: "white", fontWeight: 700 }}>{t.duration}</Typography>
                </Stack>
              )}
              {priceLabel && (
                <Stack spacing={0}>
                  <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>Fee</Typography>
                  <Typography sx={{ fontSize: 13, color: "white", fontWeight: 700 }}>{priceLabel}</Typography>
                </Stack>
              )}
              {t.enrollmentCount != null && (
                <Stack spacing={0}>
                  <Typography sx={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>Enrolled</Typography>
                  <Typography sx={{ fontSize: 13, color: "white", fontWeight: 700 }}>{t.enrollmentCount}+</Typography>
                </Stack>
              )}
            </Stack>

                        <EnrollButton label={t.ctaLabel || "Enroll now"} to={enrollHref} className="training-enroll-btn-back" />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default function TrainingSection() {
  const [mode, setMode] = useState("");
  const [activePage, setActivePage] = useState(0);
  const trackRef = useRef(null);
  const theme = useTheme();

  // Cards visible at once, per breakpoint. Desktop (lg+) shows 4, as requested.
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const cardsPerView = isXs ? 1 : isSm ? 2 : isMd ? 3 : 4;
const [atStart, setAtStart] = useState(true);
const [atEnd, setAtEnd] = useState(false);
  // Width so that exactly `cardsPerView` cards + gaps fill the track, with
  // no partial card ever poking past the edge. This calc() is applied ONLY
  // on the motion.div (the direct flex child of the track) below — never
  // inside TrainingCard itself, or the division happens twice.
  const cardWidthCss = `calc((100% - ${CARD_GAP * (cardsPerView - 1)}px) / ${cardsPerView})`;

  useEffect(() => {
  trackRef.current?.scrollTo({ left: 0 });
  setActivePage(0);
}, [cardsPerView]);

  const { data, isLoading } = useQuery({
    queryKey: ["public/trainingss", mode],
    queryFn: () => getPublicTrainings(mode ? { mode, sort: "displayOrder", order: "asc" } : { sort: "displayOrder", order: "asc" })
      .then((r) => r.data?.data ?? r.data),
  });

const allTrainings = Array.isArray(data) ? data : data?.results || [];
const trainings = allTrainings.filter((t) => t.featured);
  const totalPages = Math.max(1, Math.ceil(trainings.length / cardsPerView));

 const scrollByPage = useCallback((dir) => {
  const track = trackRef.current;
  if (!track) return;
  const firstCard = track.querySelector(".training-carousel-card");
  const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : track.clientWidth / cardsPerView;
  const step = (cardWidth + CARD_GAP) * cardsPerView;
  track.scrollBy({ left: dir * step, behavior: "smooth" });
}, [cardsPerView]);


const handleTrackScroll = useCallback(() => {
  const track = trackRef.current;
  if (!track) return;
  const max = track.scrollWidth - track.clientWidth;
  setAtStart(track.scrollLeft <= 2);
  setAtEnd(track.scrollLeft >= max - 2);
  if (track.clientWidth) {
    const page = Math.round(track.scrollLeft / track.clientWidth);
    setActivePage(Math.max(0, Math.min(page, totalPages - 1)));
  }
}, [totalPages]);

  return (
    <Box
      component="section"
      sx={{
        background: "linear-gradient(180deg, #DCE8F5 0%, #E8F0F8 45%, #F5F8FC 100%)",
        py: { xs: 7, md: 10 },
      }}
    >
      <div className="section-shell">
        <SectionHeading
          eyebrow="Training programs"
          title="Instructor-led and self-paced training built for real career outcomes."
          copy="Browse live, classroom, and self-paced formats — each with clear pricing, duration, and enrollment CTA. Hover a card to see the full details."
        />

        <Box textAlign="center" sx={{ mt: 1.5 }}>
          <Button
            component={RouterLink}
            to="/public/trainings"
            endIcon={<ArrowRight size={16} />}
            sx={{
              fontWeight: 700,
              fontSize: 14,
              textTransform: "none",
              color: "#2563EB",
              "&:hover": { bgcolor: "rgba(37,99,235,0.06)" },
            }}
          >
            View all trainings
          </Button>
        </Box>

        <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1} sx={{ mt: 4, mb: 5 }}>
          {MODE_FILTERS.map((m) => {
            const isActive = mode === m.value;
            return (
              <Chip
                key={m.value}
                label={m.label}
                onClick={() => {
                  setMode(m.value);
                  setActivePage(0);
                  trackRef.current?.scrollTo({ left: 0, behavior: "smooth" });
                }}
                sx={{
                  fontWeight: 700, fontSize: 13, borderRadius: "999px", cursor: "pointer", mr:1,
                  bgcolor: isActive ? "#2563EB" : "rgba(37,99,235,0.06)",
                  color: isActive ? "white" : "#2563EB",
                  "&:hover": { bgcolor: isActive ? "#1D4ED8" : "rgba(37,99,235,0.12)" },
                }}
              />
            );
          })}
        </Stack>

        {isLoading ? (
          <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
        ) : trainings.length === 0 ? (
          <Box textAlign="center" mt={6} color="text.secondary">
            <Typography>No trainings available right now.</Typography>
          </Box>
        ) : (
          <>
            <Box
              ref={trackRef}
              onScroll={handleTrackScroll}
              className="training-carousel-track"
              sx={{
                mt: 5,
                display: "flex",
                gap: `${CARD_GAP}px`,
                overflowX: "auto",
                overflowY: "hidden",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                pb: 1.5,
                px: { xs: 0.5, md: 0 },
                // hide scrollbar across browsers, arrows below are the primary control
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {trainings.map((t, i) => (
                <motion.div
                  key={t.id}
                  {...fadeUp(i * 0.04)}
                  className="training-flip-wrap"
                  style={{
                    display: "block",
                    flex: `0 0 ${cardWidthCss}`,
                    width: cardWidthCss,
                  }}
                >
                  <TrainingCard t={t} />
                </motion.div>
              ))}
            </Box>

            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 3 }}>
              <IconButton
                onClick={() => scrollByPage(-1)}
                disabled={atStart}
                sx={{
                  width: 40, height: 40,
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563EB",
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#EFF6FF" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronLeft size={18} />
              </IconButton>

              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0B1530", minWidth: 64, textAlign: "center" }}>
                {String(activePage + 1).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
              </Typography>

              <IconButton
                onClick={() => scrollByPage(1)}
                disabled={atEnd}
                sx={{
                  width: 40, height: 40,
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563EB",
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#EFF6FF" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Stack>
          </>
        )}
      </div>
    </Box>
  );
}